import React, { createElement, useContext, useEffect, useState } from "react";
import "./InfoComponent.scss";
import "./LinkInfoComponent.scss";
import { Settings } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import AddStationDialog from "../configuration/AddStationDialog";
import { JulianDate } from "cesium";
import ViewerContext from "../../context/ViewerContext";
import Indicator from "./Indicator";
const baseUrl = process.env.API_GATEWAY || "https://satmon.fly.dev";
let timeDiff = 0,
  dataTime;

function LinkInfoComponent({ data }) {
  const [linkData, setLinkData] = useState({});
  const [linkObjectList, setLinkObjectList] = useState({});
  const [indicatorData, setIndicatorData] = useState({});
  const [openStationDialog, setOpenStationDialog] = useState(false);
  const { dataIntervalPeriod } = useContext(ViewerContext);

  useEffect(() => {
    if (data) {
      updateInfoBox(data);
      const stationName = data.station.name;
      const satelliteId = data.satellite.id;
      const time = data.obs_time;
      fetchLinkBudgetData(stationName, satelliteId, time);
    }
  }, [data]);

  useEffect(() => {
    if (
      !linkObjectList.link_budgets ||
      linkObjectList.link_budgets.length === 0
    )
      return; // Wait until data is available

    const timeInterval = calculateTimeInterval(
      linkObjectList.obs_time_start,
      linkObjectList.obs_time_end,
      linkObjectList.link_budgets.length
    );

    // Update display every 0.1 seconds
    const intervalId = setInterval(() => {
      const dataIndex = getDataIndex(
        linkObjectList.obs_time_start,
        linkObjectList.obs_time_end,
        linkObjectList.link_budgets.length
      );

      if (!data) {
        console.log("Link data not found");
        return;
      }
      const linkBudget = linkObjectList.link_budgets[dataIndex];
      if (linkBudget) {
        const satInfo = linkBudget.satellite;
        let infoObj = { ...linkBudget };
        infoObj.celestrak_id = data.celestrak_id;
        infoObj.obs_time = dataTime;
        infoObj.station = data.station;
        infoObj.satellite = {
          id: satInfo.norad_id,
          lon: satInfo.longitude_degE,
          lat: satInfo.latitude_degN,
          height: satInfo.altitude_m,
          name: satInfo.name,
        };

        updateInfoBox(infoObj);
      }
    }, timeInterval); // Update every 0.1 seconds

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [linkObjectList]); // Run this effect when 'data' changes

  const formatObsTime = (dateStr) => {
    const date = new Date(dateStr);

    // Format the date to the desired format
    const formattedDate = date.toLocaleString("en-US", {
      weekday: "short", // "Wed"
      month: "short", // "Sep"
      day: "numeric", // "18"
      hour: "numeric", // "10"
      minute: "numeric", // "03"
      second: "numeric", // "14"
      hour12: true, // Use 12-hour time with AM/PM
    });

    return formattedDate;
  };

  const formatSatelliteHeight = (height) => {
    if (!height) return 0;
    return (height / 1000).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  function setIndicator(data) {
    const obj = {};
    obj.cnr = data.cnr_dB;
    obj.cnr_prev = indicatorData.cnr;
    obj.pathLoss = data.path_loss_dB;
    obj.pathLoss_prev = indicatorData.pathLoss;
    obj.carrier = data.power_dBm?.carrier;
    obj.carrier_prev = indicatorData.carrier;
    obj.atmosphericLoss = data.atmospheric_loss_dB;
    obj.atmosphericLoss_prev = indicatorData.atmosphericLoss;
    obj.margin = data.margin_dB;
    obj.margin_prev = indicatorData.margin;
    obj.gOverT = data.g_over_t_dB_per_K;
    obj.gOverT_prev = indicatorData.gOverT;
    obj.noise = data.noise_temp_K?.system;
    obj.noise_prev = indicatorData.noise;

    setIndicatorData({ ...obj });
  }
  function formatData(data) {
    setIndicator(data);

    const {
      name: stationName,
      latitude_degn: stationLat,
      longitude_dege: stationLon,
      altitude_m: stationAltitude,
    } = data.station;

    const {
      name: satelliteName,
      lat: satelliteLat,
      lon: satelliteLon,
      height: satelliteHeight,
    } = data.satellite;

    return {
      //Station Details
      stationName: stationName,
      stationLat: stationLat?.toFixed(3),
      stationLon: stationLon?.toFixed(3),
      stationAltitude: stationAltitude?.toFixed(0),

      // General Information

      observationTime: formatObsTime(data.obs_time),
      //Satellite Position
      satelliteName: satelliteName,
      satelliteLat: satelliteLat?.toFixed(3),
      satelliteLon: satelliteLon?.toFixed(3),
      satelliteAltitude: formatSatelliteHeight(satelliteHeight),

      pointingData: `EL:${data.pointing?.elevation_degrees.toFixed(
        2
      )}° AZ:${data.pointing?.azimuth_degrees.toFixed(
        2
      )}° PS:${data.pointing?.polarization_skew_degrees.toFixed(2)}° SR:${(
        data.pointing?.slant_range_m / 1000
      ).toFixed(0)}km`,

      // Signal Charectistics
      eirp: `${data.eirp_dBW?.toFixed(2)} dBW`,
      carrierPower: `${data.power_dBm?.carrier.toFixed(2)} dBm`,
      carrierToNoiseRatio: `${data.cnr_dB?.toFixed(2)} dB`, //Carrier-to-Noise Ratio (CNR)
      linkMargin: `${data.margin_dB?.toFixed(2)} dB`,

      // Frequency Information
      transmittedFrequency: `${(
        data.doppler_shift?.transmitted_frequency_hz / 1e9
      ).toFixed(6)} GHz`,
      observedFrequency: `${(
        data.doppler_shift?.observed_frequency_hz / 1e9
      ).toFixed(6)} GHz`,
      dopplerShift: `${(data.doppler_shift?.doppler_shift_Hz / 1e3).toFixed(
        3
      )} kHz`,

      //Path Characteristics
      pathLoss: `${data.path_loss_dB?.toFixed(2)} dB`,
      atmosphericLoss: `${data.atmospheric_loss_dB?.toFixed(2)} dB`,

      gOverT: `${data.g_over_t_dB_per_K?.toFixed(2)} dB/K`,
      systemNoiseTemperature: `${data.noise_temp_K?.system?.toFixed(2)} K`,

      // dataCapacity: `${(data.capacity_bps / 1e6).toFixed(2)} Mbps`,
    };
  }

  // Applying the format function to populate the fields
  function updateInfoBox(data) {
    const formattedData = formatData(data);
    setLinkData(formattedData);
  }

  const handleUpdateStation = () => {
    setOpenStationDialog(true);
  };

  const callbackStationFunc = (upData) => {
    data.station = upData;
    updateInfoBox(data);
  };

  async function fetchLinkBudgetData(stationName, satelliteId, time) {
    try {
      const response = await fetch(baseUrl + "/interpolated-link-budget", {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          rx_station_name: stationName,
          satellite_norad_id: satelliteId,
          start_time: time, // Replace with dynamic time if needed
          duration: dataIntervalPeriod * viewer.clock.multiplier,
          // speedup_factor: viewer.clock.multiplier,
          speedup_factor: viewer.clock.multiplier,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch link budget data");
      }

      const responseData = await response.json();
      // console.log(responseData.link_budgets);
      setLinkObjectList(responseData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }

  const calculateTimeInterval = (obsStartTime, obsEndTime, dataCount) => {
    const startTime = new Date(obsStartTime).getTime();
    const endTime = new Date(obsEndTime).getTime();
    const now = julianToDate();
    timeDiff = now - startTime;
    return (endTime - startTime) / (dataCount - 1) / viewer.clock.multiplier;
  };

  // Convert Current Time (Julian Date) to miliseconds
  function julianToDate() {
    const currentTime = viewer.clock.currentTime.clone();
    // Convert JulianDate to JavaScript Date object
    const jsDate = JulianDate.toDate(currentTime);
    // Get the milliseconds from the Date object
    return jsDate.getTime();
  }

  function getDataIndex(obs_time_start, obs_time_end, dataCount) {
    // Parse the timestamps to miliseconds
    const startTime = new Date(obs_time_start).getTime();
    const endTime = new Date(obs_time_end).getTime();
    const now = julianToDate() - timeDiff;
    // If the current time is outside the range, return the closest index
    if (now <= startTime) return 0;
    if (now >= endTime) return dataCount - 1;

    // Calculate the total duration between the start and end times
    const totalDuration = endTime - startTime;

    // Calculate the time elapsed since the start time
    const timeElapsed = now - startTime;

    // Determine the percentage of the elapsed time in the total duration
    const percentage = timeElapsed / totalDuration;

    // Map the percentage to an index in the range of 0 to dataCount
    const dataIndex = Math.floor(percentage * (dataCount - 1));
    //convert dataTime to Iso8601
    dataTime = obs_time_start;
    try {
      const julianDate = JulianDate.fromDate(new Date(now));
      dataTime = JulianDate.toIso8601(julianDate);
    } catch (error) {
      console.log(error);
    }

    return dataIndex;
  }

  return (
    <>
      <div id="data-container">
        <div className="infobox-flex-row">
          Link {linkData.stationName} - {linkData.satelliteName}
          <Tooltip title="Edit Station" arrow>
            <IconButton
              className="settings-button"
              sx={{ padding: "0px" }}
              onClick={() => handleUpdateStation()}
            >
              <Settings sx={{ color: "#fff" }} />
            </IconButton>
          </Tooltip>
        </div>
        <div className="centered-line"></div>

        <div className="infobox-row">{linkData.observationTime}</div>
        <div className="centered-line"></div>

        <div className="infobox-row">
          <span className="first-item">{linkData.stationName}</span>
          <span className="second-item">{`${linkData.stationLat}°N, ${linkData.stationLon}°W, EL ${linkData.stationAltitude}M`}</span>
        </div>
        <div className="infobox-row">
          <span className="first-item xsmall-item">
            {linkData.satelliteName}
          </span>
          <span className="second-item">{`${linkData.satelliteLat}°N, ${linkData.satelliteLon}°W, ${linkData.satelliteAltitude}KM`}</span>
        </div>
        <div className="infobox-row">
          <span className="first-item">Pointing</span>
          <span className="second-item">{linkData.pointingData}</span>
        </div>
        <div className="centered-line"></div>

        <div className="infobox-row">
          <span className="first-item">Frequency</span>
          <span className="second-item wrap-item">
            <span>{`Tx:${linkData.transmittedFrequency}`}</span>
            <span>{`Rx:${linkData.observedFrequency}`}</span>
            <span>{`Δf:+${linkData.dopplerShift}`}</span>
          </span>
        </div>
        <div className="centered-line"></div>
        <div className="infobox-row">
          <span className="first-item">Link Budget</span>
          <span className="second-item wrap-item">
            <span>{`EIRP:${linkData.eirp}`}</span>
            <span>
              {`Carrier:${linkData.carrierPower}`}
              <Indicator
                currentVal={indicatorData.carrier}
                previousVal={indicatorData.carrier_prev}
              />
            </span>
            <span>
              {`C/N:${linkData.carrierToNoiseRatio}`}
              <Indicator
                currentVal={indicatorData.cnr}
                previousVal={indicatorData.cnr_prev}
              />
            </span>

            <span>
              {`Loss:${linkData.pathLoss}`}
              <Indicator
                currentVal={indicatorData.pathLoss}
                previousVal={indicatorData.pathLoss_prev}
                trend="lower"
              />
            </span>
            <span>
              {`Atmos:${linkData.atmosphericLoss}`}
              <Indicator
                currentVal={indicatorData.atmosphericLoss}
                previousVal={indicatorData.atmosphericLoss_prev}
                trend="lower"
              />
            </span>
            <span>
              {`Margin:${linkData.linkMargin}`}
              <Indicator
                currentVal={indicatorData.margin}
                previousVal={indicatorData.margin_prev}
              />
            </span>
            <span>
              {`G/T:${linkData.gOverT}`}
              <Indicator
                currentVal={indicatorData.gOverT}
                previousVal={indicatorData.gOverT_prev}
              />
            </span>
            <span>
              {`Noise:${linkData.systemNoiseTemperature}`}
              <Indicator
                currentVal={indicatorData.noise}
                previousVal={indicatorData.noise_prev}
                trend="lower"
              />
            </span>
          </span>
        </div>
      </div>
      {openStationDialog && (
        <AddStationDialog
          openDialog={openStationDialog}
          setOpenDialog={setOpenStationDialog}
          stationObject={data.station}
          celestrakId={data.celestrak_id}
          callbackFunc={callbackStationFunc}
        />
      )}
    </>
  );
}

export default LinkInfoComponent;
